import type Experiments from '@wix/wix-experiments';
import type {
  ManagedApps,
  Services,
  AppBadge,
  PremiumAssets,
} from '@wix/app-market-services';
import type { ISiteContext } from '@wix/app-market-core';
import {
  AppNotificationType,
  type AppNotification,
  type GetEditorComponents,
} from './types';
import { hasBenefitOrBundleBadge } from '../../utils/badges';
import { RESELLER_FLOW_APPS } from '../../reseller-flow-consts';

export async function getAppNotification({
  services,
  managedApps,
  app,
  siteContext,
  experiments,
  premiumAssets,
  isAppCompatible,
}: {
  services: Services;
  managedApps?: ManagedApps;
  app: {
    id: string;
    name: string;
    isPremiumSiteRequired: boolean;
    dependencies: {
      id: string;
      slug: string;
      name: string;
      isPublished: boolean;
    }[];
    supportedInEditorX: boolean;
    supportedInStudio: boolean;
    badges: AppBadge[];
  };
  siteContext: ISiteContext;
  experiments: Experiments;
  premiumAssets?: PremiumAssets;
  isAppCompatible: boolean;
}): Promise<AppNotification | null> {
  const isEnableAppIncompatibleNotification = experiments?.enabled(
    'specs.dcc.app-installer.validationForPublishedApps',
  );
  if (isEnableAppIncompatibleNotification && !isAppCompatible) {
    return {
      type: AppNotificationType.AppIncompatible,
      data: {
        appId: app.id,
        appName: app.name,
      },
    };
  }

  const isEnableHeadlessAppNotification = experiments?.enabled(
    'specs.marketplace.headless-app-notification',
  );
  if (
    isEnableHeadlessAppNotification &&
    (await isShowHeadlessAppNote({
      managedApps,
      services,
      appId: app.id,
      getEditorComponents: services.getAppsRuntimeData,
    }))
  ) {
    return {
      type: AppNotificationType.HeadlessAppInstalled,
      data: {
        appName: app.name,
      },
    };
  }

  if (
    (siteContext?.editorType === 'STUDIO' && !app.supportedInStudio) ||
    (siteContext?.editorType === 'EDITORX' && !app.supportedInEditorX)
  ) {
    return {
      type: AppNotificationType.EditorNotSupported,
      data: {
        appName: app.name,
        editorType: siteContext?.editorType,
      },
    };
  }

  if (
    app.isPremiumSiteRequired &&
    !siteContext.isPremium &&
    siteContext.metaSiteId
  ) {
    return {
      type: AppNotificationType.PremiumSiteRequired,
      data: {
        app: {
          id: app.id,
          name: app.name,
        },
      },
    };
  }

  const managedApp = managedApps?.findByAppId?.(app.id);
  const appDashboard = managedApp?.getAppDashboard?.();
  if (managedApp?.isAppPendingConfiguration?.() && appDashboard) {
    return {
      type: AppNotificationType.SetupIncomplete,
      data: {
        appId: app.id,
        dashboardUrl: appDashboard.url,
        isExternalDashboard: appDashboard.isExternal,
      },
    };
  }

  const publishedAppDependencies = app.dependencies.filter(
    (appDependency) => appDependency.isPublished,
  );
  const isAllowUnmetDependencies = experiments?.enabled(
    'specs.app-market.AmcAllowUnmetDependencies',
  );
  const hasOnlyUnmetDependencies =
    managedApps &&
    publishedAppDependencies.length > 0 &&
    app.dependencies.every(
      (appDependency) => !managedApps.isAppInstalled(appDependency.id),
    );
  if (
    hasOnlyUnmetDependencies &&
    !isAllowUnmetDependencies &&
    publishedAppDependencies.length > 1
  ) {
    return {
      type: AppNotificationType.DependenciesNotInstalled,
      data: {
        app,
        appDependencies: publishedAppDependencies,
      },
    };
  }

  const pendingEditorAppDependency = app.dependencies.find((appDependency) => {
    return isAppDependencyPendingEditor({
      managedApps,
      appDependency,
    });
  });

  if (
    pendingEditorAppDependency &&
    !appHasInstalledDependencyNotPendingEditor({
      appDependencies: app.dependencies,
      managedApps,
    })
  ) {
    return {
      type: AppNotificationType.DependencyPendingEditor,
      data: {
        app,
        appDependency: pendingEditorAppDependency,
      },
    };
  }

  if (hasBenefitOrBundleBadge({ appBadges: app.badges })) {
    return {
      type: AppNotificationType.AppBenefit,
      data: {
        appName: app.name,
      },
    };
  }

  const resellerFlowApp = RESELLER_FLOW_APPS.find(
    ({ appId }) => appId === app.id,
  );
  if (
    experiments.enabled('specs.app-market.ResellerFlow') &&
    resellerFlowApp &&
    siteContext.metaSiteId &&
    !managedApps?.isAppInstalled(app.id) &&
    !premiumAssets?.isEmpty()
  ) {
    return {
      type: AppNotificationType.ResellerFlow,
      data: {
        appName: app.name,
      },
    };
  }

  return null;
}

function appHasInstalledDependencyNotPendingEditor({
  appDependencies,
  managedApps,
}: {
  appDependencies: {
    id: string;
    slug: string;
    name: string;
  }[];
  managedApps: ManagedApps | undefined;
}): boolean {
  return appDependencies.some((appDependency) => {
    return Boolean(
      managedApps &&
        managedApps.isAppInstalled(appDependency.id) &&
        !managedApps.findByAppId(appDependency.id).isAppPendingEditor(),
    );
  });
}

async function isShowHeadlessAppNote({
  services,
  appId,
  managedApps,
  getEditorComponents,
}: {
  services: Services;
  appId: string;
  managedApps: ManagedApps | undefined;
  getEditorComponents: GetEditorComponents;
}): Promise<boolean> {
  if (isHeadlessAppInstalled({ managedApps })) {
    const [isAppABusinessSolution, isAppHaveEditorComponent] =
      await Promise.all([
        appIsABusinessSolution({ services, appId }),
        appHaveEditorComponent({ getEditorComponents, appId }),
      ]);
    return !isAppABusinessSolution && isAppHaveEditorComponent;
  }

  return false;
}

function isHeadlessAppInstalled({
  managedApps,
}: {
  managedApps: ManagedApps | undefined;
}): boolean {
  return Boolean(
    managedApps &&
      managedApps.isAppInstalled('78640cbb-be47-45f8-b6de-8ed97287872c'),
  );
}

async function appHaveEditorComponent({
  getEditorComponents,
  appId,
}: {
  getEditorComponents: GetEditorComponents;
  appId: string;
}): Promise<boolean> {
  const appsComponents = await getEditorComponents({
    appId,
    componentTypes: [
      'EMBEDDED_SCRIPT',
      'WIDGET',
      'STUDIO_WIDGET',
      'WIDGET_OUT_OF_IFRAME',
      'PAGE',
    ],
  });
  const appComponents = appsComponents.findByAppId(appId);
  return Boolean(appComponents.components.length);
}

async function appIsABusinessSolution({
  services,
  appId,
}: {
  services: Services;
  appId: string;
}): Promise<boolean> {
  const { appIds } = await services.queryTagApp({
    appIds: [appId],
    tagIds: ['db25f881-7bf3-4158-94da-ffae263f1dab'],
  });
  return Boolean(appIds.length);
}

function isAppDependencyPendingEditor({
  managedApps,
  appDependency,
}: {
  managedApps: ManagedApps | undefined;
  appDependency: {
    id: string;
    slug: string;
    name: string;
  };
}): boolean {
  return Boolean(
    managedApps &&
      managedApps.findByAppId(appDependency.id).isAppPendingEditor(),
  );
}
